/** @jsxImportSource @emotion/react */

import { FC, useEffect, useRef, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  useGetTwilioConversationsQuery,
  useGetTwilioMediaUrlLazyQuery,
  useGetTwilioWaMessagesQuery,
  useOnConversationUpdatedSubscription,
  useSendTwilioWaMessageMutation,
} from "../../graphql/__GENERATED__/types";
import { useErrorHandler } from "../../hooks/useErrorHandler";
import ChatInput from "./components/ChatInput";
import Message from "./components/Message";
import ScrollContainer from "../ScrollContainer";
import { formatPhone } from "../../utils/formatNumber";
import { theme } from "../../theme";
import Typography from "../Typography";
import serviceFetch from "../../services/fetch";
import { ObjectId } from "bson";
import PageTitle from "../PageTitle";
import { CSSObject } from "@emotion/react";
import CustomLink from "../CustomLink";
import { ToDocumentDetailsLocation } from "../../Locations";
import { IconNames } from "../Icons/styles/iconNames";
import Button from "../Button";
import ChatList from "./components/ChatList";
import LoadingSpinner from "../Loading/LoadingSpinner";
import LoadingOverlay from "../Loading/LoadingOverlay";

const styles: CSSObject = {
  chatWrapper: {
    display: "flex",
    height: "100%",
  },
  conversationWrapper: {
    height: "100%",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    borderRadius: "6px",
    width: "100%",
    position: "relative",
  },
  chatListWrapper: {
    width: "30%",
    maxWidth: "700px",
    minWidth: "300px",
    borderRight: `1px solid ${theme.colors.Grey[60]}`,
    padding: "16px",
    boxSizing: "border-box",
    height: "100%",
  },
  header: {},
  contactData: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: theme.colors.Green[40],
    padding: "10px",
  },
  center: {
    flex: 1,
    textAlign: "center",
  },
  end: {
    marginLeft: "auto",
  },
};
export interface WAChatProps {
  contactId: string;
  showChatList?: boolean;
}
const WAChat: FC<WAChatProps> = ({ contactId, showChatList }) => {
  const auth = useAuth();
  const [message, setMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [files, setFiles] = useState<File[]>([]);

  const { data, refetch } = useGetTwilioWaMessagesQuery({
    variables: {
      contactId: contactId!,
    },
  });

  const getConversationsQuery = useGetTwilioConversationsQuery({
    variables: {},
  });

  const [getTwilioMediaUrlQuery] = useGetTwilioMediaUrlLazyQuery();
  const getMediaUrl = async (mediaId: string) => {
    try {
      const { data } = await getTwilioMediaUrlQuery({
        variables: {
          mediaId,
        },
      });
      window.open(data!.getTwilioMediaUrl as unknown as string, "_blank");
      return data;
    } catch (e) {
      errorHandler(
        new Error("Não foi possível fazer o download desse arquivo"),
        e
      );
    }
  };

  const [sendMessageMutation] = useSendTwilioWaMessageMutation();
  const { errorHandler } = useErrorHandler();

  const fullyRefresh = async () => {
    await refetch();
    await getConversationsQuery.refetch();
  };

  const sendMessage = async () => {
    setSendingMessage(true);
    try {
      const filePayload = await Promise.all(
        files.map(async (file) => {
          const fileKey = `conversation/${contactId}/${new ObjectId().toString()}`;
          await uploadFile(file, fileKey);
          return { fileKey, fileName: file.name };
        })
      );
      await sendMessageMutation({
        variables: {
          contactId: contactId!,
          content: message,
          files: filePayload,
        },
      });
      await fullyRefresh();
      setMessage("");
      setFiles([]);
    } catch (e) {
      errorHandler(new Error("Não foi possível enviar sua mensagem"), e);
    }
    setSendingMessage(false);
  };

  const chatBoxRef = useRef<HTMLDivElement>(null); // Reference for the chatBox container

  useEffect(() => {
    if (chatBoxRef.current) {
      const element = chatBoxRef.current;
      element.scrollTop = element.scrollHeight;
    }
  }, [data?.getTwilioWAMessages.messages]); // Dependency on messages array

  useEffect(() => {
    fullyRefresh();
  }, [contactId]);

  useOnConversationUpdatedSubscription({
    onData: ({ data: { data: subData } }) => {
      if (subData?.conversationUpdated?.contactId == contactId) {
        fullyRefresh();
      }
    },
  });

  const {
    getTwilioWAMessages: { contact, messages, documentIds },
  } = data || { getTwilioWAMessages: {} };

  async function uploadFile(file: File, fileKey: string): Promise<void> {
    await serviceFetch(
      auth,
      `upload-file`,
      null,
      "post",
      {
        fileKey,
      },
      file
    );
  }

  const mostRecentDocId = documentIds?.[0];

  const onFilesAccepted = (filesAdded: File[]) => {
    setFiles([...files, ...filesAdded]);
  };

  const contactLabel = contact
    ? formatPhone(contact.mobilePhone!) +
      (contact.name ? ` - ${contact.name}` : "")
    : "";

  return (
    <div css={styles.chatWrapper}>
      <PageTitle title={`Conversa  - ${contactLabel}`} />
      {showChatList && (
        <div css={styles.chatListWrapper}>
          <ScrollContainer>
            <ChatList
              selectedContactId={contactId}
              query={getConversationsQuery}
            />
          </ScrollContainer>
        </div>
      )}
      <div css={styles.conversationWrapper}>
        {!data?.getTwilioWAMessages && (
          <LoadingOverlay>
            <LoadingSpinner />
          </LoadingOverlay>
        )}
        {contact && (
          <div css={styles.contactData}>
            <div css={styles.center}>
              <Typography variant="textLarge" color="White" weight="bold">
                {formatPhone(contact.mobilePhone!)}
                {contact.name ? ` - ${contact.name}` : ""}
              </Typography>
            </div>
            <div css={styles.end}>
              {!!mostRecentDocId && (
                <CustomLink to={ToDocumentDetailsLocation(mostRecentDocId)}>
                  <Button text="Documento" iconName={IconNames.Share} />
                </CustomLink>
              )}
            </div>
          </div>
        )}
        <div style={{ padding: "10px 0" }}></div>
        <ScrollContainer useAnchor>
          {messages?.map((msg) => (
            <Message message={msg} key={msg._id} getMediaUrl={getMediaUrl} />
          ))}
        </ScrollContainer>
        <ChatInput
          message={message}
          setMessage={setMessage}
          sendMessage={sendMessage}
          loading={sendingMessage}
          onFilesAccepted={onFilesAccepted}
          files={files}
          setFiles={setFiles}
        />
      </div>
    </div>
  );
};

export default WAChat;
